import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { AdminCodeStep } from "../components/AdminCodeStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() =>
    new URLSearchParams(window.location.search).get("expired")
      ? "You were signed out after 30 minutes of inactivity. Please sign in again."
      : ""
  );
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.code_required) {
        setPassword("");
        setChallenge({ id: data.challenge_id, minutes: data.code_minutes });
      } else navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A2333] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <a href="/" data-testid="admin-login-brand" className="block">
          <span className="font-serif text-3xl tracking-wide text-[#F9F8F4]">NUA</span>
          <span className="block mt-2 text-[10px] uppercase tracking-[0.4em] text-[#A78558]">Private Wealth</span>
        </a>
        <h1 className="mt-10 font-serif text-3xl tracking-tight text-[#F9F8F4]">Adviser sign in</h1>
        <p className="mt-3 text-sm text-[#F9F8F4]/60">Private access to client enquiries.</p>

        {challenge ? (
          <AdminCodeStep
            challengeId={challenge.id}
            minutes={challenge.minutes}
            formatError={formatApiError}
            onDone={() => navigate("/admin")}
            onBack={() => setChallenge(null)}
          />
        ) : (
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-sm text-[#F9F8F4] placeholder:text-white/40 transition-[border-color] duration-300"
              placeholder="you@nuawealth.com.au"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-sm text-[#F9F8F4] placeholder:text-white/40 transition-[border-color] duration-300"
              placeholder="••••••••••••"
            />
          </div>

          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#d98a72] border border-[#A78558]/40 px-4 py-3">
              {error}
            </p>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-[#F9F8F4] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading ? (
              <>
                Signing in
                <Loader2 size={15} className="animate-spin" />
              </>
            ) : (
              <>
                Sign in
                <ArrowRight size={15} />
              </>
            )}
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
